import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { useNavigate, useRouteError } from 'react-router-dom';
import { RefreshCw, ServerCrash } from 'lucide-react';
import { ApiError } from '@/api/client';
import { Button } from '@/design-system/Button';
import { ErrorState } from '@/design-system/Feedback';

/**
 * Boundaries so one broken screen does not take the application with it.
 *
 * A crash inside a route renders a recoverable panel with the request id, rather than a blank
 * page - which tells the user nothing and support even less.
 */
interface State {
  error: Error | null;
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Sent to the browser console so it reaches whatever front-end telemetry is configured. It
    // deliberately carries no user data beyond the stack.
    console.error('Unhandled UI error', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="grid min-h-screen place-items-center bg-background p-6">
        <div className="w-full max-w-md rounded-xl border border-border-subtle bg-surface p-8 text-center">
          <div
            aria-hidden
            className="mx-auto mb-4 grid size-12 place-items-center rounded-full border border-danger-border bg-danger-subtle text-danger-text"
          >
            <ServerCrash className="size-5" />
          </div>
          <h1 className="text-h2 text-text-primary">Something went wrong</h1>
          <p className="mt-2 text-small text-text-muted">
            The page could not be displayed. Reloading usually resolves it; if it keeps happening,
            report the message below.
          </p>
          <p className="mt-3 rounded-md bg-surface-sunken px-3 py-2 font-mono text-caption break-words text-text-secondary">
            {this.state.error.message}
          </p>
          <Button
            variant="primary"
            className="mt-5"
            onClick={() => window.location.reload()}
            leadingIcon={<RefreshCw className="size-4" />}
          >
            Reload
          </Button>
        </div>
      </div>
    );
  }
}

/** Route-level boundary, so a failing route keeps the shell and navigation intact. */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  return (
    <div className="mx-auto max-w-2xl py-10">
      <ErrorState
        error={error instanceof ApiError ? error : (error as Error)}
        onRetry={() => navigate(0)}
        context="this page"
      />
      <div className="mt-4 flex justify-center">
        <Button variant="ghost" onClick={() => navigate('/dashboard')}>
          Go to dashboard
        </Button>
      </div>
    </div>
  );
}
